/**
 * depthBokeh.js — On-device depth estimation → portrait bokeh
 * Model: onnx-community/depth-anything-v2-small (q8, ~44 MB, cached after first load)
 * Pipeline: AutoModelForDepthEstimation → depth map → canvas blend
 */

import {
  AutoModelForDepthEstimation,
  AutoProcessor,
  env,
  RawImage,
} from "@huggingface/transformers";

env.allowLocalModels = false;
env.useBrowserCache  = true;

const MODEL_ID = "onnx-community/depth-anything-v2-small";
let _model    = null;
let _processor = null;
let _loading  = null;

// ── Model loading ─────────────────────────────────────────────────────────────

export async function loadDepthModel(onProgress) {
  if (_model && _processor) return;
  if (_loading) return _loading;

  _loading = (async () => {
    const cb = (p) => {
      if (typeof onProgress !== "function") return;
      if (p?.status === "progress_total") {
        onProgress(Math.round(p.progress ?? 0));
      } else if (p?.status === "progress" && p?.total > 0) {
        onProgress(Math.round((p.loaded / p.total) * 100));
      }
    };

    [_model, _processor] = await Promise.all([
      AutoModelForDepthEstimation.from_pretrained(MODEL_ID, {
        dtype: "q8",
        progress_callback: cb,
      }),
      AutoProcessor.from_pretrained(MODEL_ID),
    ]);
    _loading = null;
  })();

  return _loading;
}

export function isDepthModelLoaded() {
  return Boolean(_model && _processor);
}

// ── Depth map inference ───────────────────────────────────────────────────────

/**
 * Run depth estimation on a blob.
 * Returns a grayscale canvas (255=far, 0=near) at the model's output resolution.
 */
export async function getDepthMap(blob) {
  if (!_model || !_processor) throw new Error("Depth model not loaded");

  const url   = URL.createObjectURL(blob);
  const image = await RawImage.fromURL(url);
  URL.revokeObjectURL(url);

  const { pixel_values }   = await _processor(image);
  const { predicted_depth } = await _model({ pixel_values });

  // predicted_depth[0] → Tensor [H, W] (already batch-indexed by the model class)
  const tensor = predicted_depth[0];
  const H      = tensor.dims[0];
  const W      = tensor.dims[1];
  const raw    = tensor.data; // Float32Array, length = H * W

  // Normalise: min→0, max→255 (large raw value = far = bright = more blur)
  let min = Infinity, max = -Infinity;
  for (const v of raw) { if (v < min) min = v; if (v > max) max = v; }
  const range = max - min || 1;

  const canvas = document.createElement("canvas");
  canvas.width  = W;
  canvas.height = H;
  const ctx    = canvas.getContext("2d");
  const imgData = ctx.createImageData(W, H);

  for (let i = 0; i < H * W; i++) {
    const v = Math.round(((raw[i] - min) / range) * 255);
    imgData.data[i * 4]     = v;
    imgData.data[i * 4 + 1] = v;
    imgData.data[i * 4 + 2] = v;
    imgData.data[i * 4 + 3] = 255;
  }
  ctx.putImageData(imgData, 0, 0);

  return { depthCanvas: canvas, origW: image.width, origH: image.height };
}

// ── Bokeh compositing ─────────────────────────────────────────────────────────

const WORK_MAX = 1280; // process at this resolution for speed

/**
 * Blend original + blurred image using depth as a mask.
 * depth=255 (far) → blurred; depth=0 (near) → sharp.
 * @param {Blob} blob           Original photo blob
 * @param {HTMLCanvasElement} depthCanvas  Grayscale depth canvas from getDepthMap
 * @param {number} blurStrength  CSS blur radius in px (default 16)
 * @returns {Promise<Blob>} JPEG blob
 */
export async function applyDepthBokeh(blob, depthCanvas, blurStrength = 16) {
  const origImg = await blobToImage(blob);

  // Work at capped resolution
  const scale = Math.min(1, WORK_MAX / Math.max(origImg.width, origImg.height));
  const W     = Math.round(origImg.width  * scale);
  const H     = Math.round(origImg.height * scale);

  // Sharp layer
  const sharpC = makeCanvas(W, H);
  sharpC.getContext("2d").drawImage(origImg, 0, 0, W, H);

  // Blurred layer (CSS filter — fast, hardware-accelerated)
  const blurC  = makeCanvas(W, H);
  const blurCtx = blurC.getContext("2d");
  blurCtx.filter = `blur(${blurStrength}px)`;
  blurCtx.drawImage(origImg, 0, 0, W, H);
  blurCtx.filter = "none";

  // Depth layer scaled to working resolution
  const depthC = makeCanvas(W, H);
  depthC.getContext("2d").drawImage(depthCanvas, 0, 0, W, H);

  const sharpPx = readPixels(sharpC, W, H);
  const blurPx  = readPixels(blurC,  W, H);
  const depthPx = readPixels(depthC, W, H);

  // Blend per-pixel with smoothstep for a natural falloff
  const out = new ImageData(W, H);
  for (let i = 0; i < W * H; i++) {
    const d = depthPx[i * 4] / 255;              // 0=near, 1=far
    const t = d * d * (3 - 2 * d);               // smoothstep
    const p = i * 4;
    out.data[p]     = (sharpPx[p]     + (blurPx[p]     - sharpPx[p])     * t + 0.5) | 0;
    out.data[p + 1] = (sharpPx[p + 1] + (blurPx[p + 1] - sharpPx[p + 1]) * t + 0.5) | 0;
    out.data[p + 2] = (sharpPx[p + 2] + (blurPx[p + 2] - sharpPx[p + 2]) * t + 0.5) | 0;
    out.data[p + 3] = 255;
  }

  const final = makeCanvas(W, H);
  final.getContext("2d").putImageData(out, 0, 0);
  return canvasToBlob(final, "image/jpeg", 0.92);
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function makeCanvas(w, h) {
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  return c;
}

function readPixels(canvas, w, h) {
  return canvas.getContext("2d", { willReadFrequently: true })
    .getImageData(0, 0, w, h).data;
}

function canvasToBlob(canvas, type = "image/jpeg", quality = 0.92) {
  return new Promise((res, rej) =>
    canvas.toBlob((b) => (b ? res(b) : rej(new Error("toBlob failed"))), type, quality)
  );
}

function blobToImage(blob) {
  return new Promise((res, rej) => {
    const img = new Image();
    const u   = URL.createObjectURL(blob);
    img.onload  = () => { URL.revokeObjectURL(u); res(img); };
    img.onerror = () => { URL.revokeObjectURL(u); rej(new Error("Image load failed")); };
    img.src = u;
  });
}
